import { useMemo, useState } from 'react';
import { MonthlyBarChart } from '@/components/charts/MonthlyBarChart';
import { formatCurrencyAmount } from '@/lib/formatMoney';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

export interface MrrMovementRow {
  month: string;
  basis: 'exact' | 'approximated';
  byCurrency: Record<string, { new: number; churned: number; net: number }>;
}

/**
 * One currency at a time is deliberate: plotting several on one axis would
 * invite comparing them, and currencies are never summed on this surface.
 *
 * `new` and `churned` are gross, group-level figures (the same technique the
 * Churn panel uses): MRR carried by groups that actually joined or left in
 * the month. They are NOT the positive/negative halves of the net MRR change,
 * so `new - churned` will not generally equal `net`. The gap is MRR movement
 * inside groups that stayed subscribed all month (a seat added, a plan
 * upgrade or downgrade): real money, but not attributable to a specific
 * group-level event without a subscription status history the schema does
 * not have (see SubscriptionTimeline's class docblock).
 *
 * That gap IS derivable in aggregate, though, as plain arithmetic on the
 * three numbers the API already sends: `retained = net - new + churned`.
 * Leaving it off the chart would mean the reader never sees the total the
 * caption below is talking about, so it is plotted as its own bar, and
 * `new - churned + retained` always equals `net` by construction. This is an
 * aggregate residual for the currency as a whole, not a per-group split: it
 * cannot say WHICH retained group expanded or contracted, only that the
 * retained cohort's total moved by this much.
 *
 * The currency picker defaults to whichever currency has the most total
 * activity, ranked by Σ(new + churned), the two series this chart actually
 * plots, not by Σ|net|. A currency with 500k in and 500k out nets to zero and
 * would sort last under a net-based ranking despite being the most active
 * thing on the page. Deliberately NOT converted to a common currency to rank
 * fairly across currencies: currencies are never summed or converted on this
 * surface, so a currency with a larger nominal unit scale (UGX vs USD, say)
 * will generally outrank a smaller-scale one by raw magnitude alone. That is
 * an accepted property of "default to the currency with the most going on",
 * not a bug.
 */
export function MrrMovementChart({ rows }: { rows: MrrMovementRow[] }) {
  const currencies = useMemo(() => {
    const totals: Record<string, number> = {};
    for (const row of rows) {
      for (const [currency, movement] of Object.entries(row.byCurrency)) {
        totals[currency] = (totals[currency] ?? 0) + movement.new + movement.churned;
      }
    }
    return Object.keys(totals).sort((a, b) => totals[b] - totals[a] || a.localeCompare(b));
  }, [rows]);

  const [selected, setSelected] = useState<string | undefined>(undefined);
  const currency = selected ?? currencies[0];

  if (currencies.length === 0) {
    return <p className="py-8 text-center text-sm text-muted-foreground">No recurring revenue movement yet.</p>;
  }

  const data = rows.map((row) => {
    const movement = row.byCurrency[currency] ?? { new: 0, churned: 0, net: 0 };
    return {
      month: row.month,
      new: movement.new,
      churned: movement.churned,
      // Aggregate residual, not a per-group figure: see the class doc
      // comment above. Can be negative (the retained cohort contracted on
      // net) as easily as positive, unlike new/churned which are always
      // reported as non-negative magnitudes.
      retained: movement.net - movement.new + movement.churned,
    };
  });

  // Reconstructed from subscription timestamps, same as the Churn panel: a
  // month resting on an inferred cancellation must say so rather than
  // presenting an approximation as a fact.
  const hasApproximatedMonth = rows.some((row) => row.basis === 'approximated');

  return (
    <div className="space-y-4" data-testid="mrr-movement-chart">
      <Select value={currency} onValueChange={setSelected}>
        <SelectTrigger className="w-[160px]">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {currencies.map((code) => (
            <SelectItem key={code} value={code}>
              {code}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <MonthlyBarChart
        data={data}
        series={[
          { key: 'new', label: 'New MRR' },
          { key: 'churned', label: 'Churned MRR' },
          { key: 'retained', label: 'Retained group change' },
        ]}
        valueFormatter={(value) => formatCurrencyAmount(value, currency)}
      />

      <p className="text-xs text-muted-foreground">
        New and churned count groups that joined or left. Retained group change is the remaining, aggregate movement
        inside groups that stayed subscribed all month (upgrades and downgrades netted together, not attributed to
        any one group). New minus churned plus retained group change equals the total change in MRR for the month.
      </p>

      {hasApproximatedMonth && (
        <p className="text-xs text-muted-foreground">
          Reconstructed from subscription records. Figures for past months are approximate.
        </p>
      )}
    </div>
  );
}
